import React from 'react';
import { Box, Typography } from '@mui/material';
import { Loading } from '../loading';

export type EmptyStateType = 'loading' | 'error' | 'no-data' | 'no-results' | 'no-center' | 'missing-params';

export interface EmptyStateProps {
  /** The type of empty state to display */
  type: EmptyStateType;
  /** Custom message to display (overrides default) */
  message?: string;
  /** Secondary message below main message */
  subMessage?: string;
  /** Minimum height for the container */
  minHeight?: string | number;
}

const defaultMessages: Record<EmptyStateType, string> = {
  loading: 'Loading...',
  error: 'Error loading data. Please try again.',
  'no-data': 'No data found.',
  'no-results': 'No results match your search.',
  'no-center': 'Please select a center to continue.',
  'missing-params': 'Please select required parameters.',
};

/**
 * Unified empty state component for grids and content areas.
 * Delegates loading state to the shared Loading component.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  type,
  message,
  subMessage,
  minHeight = '300px',
}) => {
  const displayMessage = message || defaultMessages[type];

  if (type === 'loading') {
    return <Loading variant="inline" message={displayMessage} subMessage={subMessage} minHeight={minHeight} />;
  }

  if (type === 'error') {
    return (
      <Box minHeight={minHeight} display="flex" alignItems="center" justifyContent="center">
        <Typography color="error">{displayMessage}</Typography>
      </Box>
    );
  }

  // no-data, no-results, no-center, missing-params
  return (
    <Box sx={{ textAlign: 'center', py: 8, minHeight }}>
      <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
        {displayMessage}
      </Typography>
      {subMessage && (
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          {subMessage}
        </Typography>
      )}
    </Box>
  );
};

export default EmptyState;
